import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Mail, X } from "lucide-react";
import { toast } from "sonner";

/** Composer et envoyer un e-mail (individuel ou collectif) directement depuis le site. */
export default function AdminEmailDialog({ recipients, defaultSubject = "", buttonClassName = "", buttonLabel, testId = "admin-email" }) {
  const list = (Array.isArray(recipients) ? recipients : [recipients]).filter((e) => e && String(e).includes("@"));
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const disabled = list.length === 0;

  const send = async () => {
    if (subject.trim().length < 2 || body.trim().length < 5) { toast.error("Sujet et message requis."); return; }
    setSending(true);
    try {
      const { data } = await api.post("/admin/email/send", { recipients: list, subject: subject.trim(), body: body.trim() });
      toast.success(`E-mail envoyé à ${data.sent} destinataire(s)${data.failed ? ` · ${data.failed} échec(s)` : ""}`);
      setOpen(false); setBody("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Envoi impossible"); }
    finally { setSending(false); }
  };

  return (
    <>
      <button type="button" onClick={() => !disabled && setOpen(true)} disabled={disabled}
              className={buttonClassName || `flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold ${disabled ? "bg-slate-100 text-slate-400 pointer-events-none" : "bg-slate-900 text-white hover:bg-slate-800"}`}
              data-testid={`${testId}-open`}>
        {buttonLabel || <><Mail size={12}/> Email</>}
      </button>
      {open && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white w-full max-w-lg rounded-2xl p-5" onClick={(e) => e.stopPropagation()} data-testid={`${testId}-dialog`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-lg font-bold">Écrire un e-mail{list.length > 1 ? ` (${list.length} destinataires)` : ""}</h3>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg hover:bg-slate-100"><X size={18}/></button>
            </div>
            <div className="text-xs text-slate-500 mb-2 break-all"><span className="font-semibold">À :</span> {list.join(", ")}</div>
            <Label>Sujet</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid={`${testId}-subject`}/>
            <Label className="mt-3 block">Message</Label>
            <Textarea rows={7} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Votre message…" data-testid={`${testId}-body`}/>
            <div className="mt-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-2">
              Envoi via le service e-mail PROXIreduc-28 — aucun logiciel ni site externe n'est ouvert.
            </div>
            <div className="mt-4 flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button disabled={sending} onClick={send} className="btn-shine text-white" data-testid={`${testId}-send`}>{sending ? "Envoi…" : "Envoyer"}</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
